"use client"
import { useState, useMemo, useEffect, useCallback } from "react"
import { Badge } from "@/components/ui/badge"
import { CloudRain, Users, Wrench, Clock, ChevronLeft, ChevronRight, X } from "lucide-react"
import { mockEvents, type CalendarEvent } from "./data/mockData"
import { getCalendarGanttData, CalendarGanttEvent, CalendarGanttDay, ContinuousBar } from "./data/calendarGanttService"
import { useProject } from "@/components/ProjectSelector"
import { saveGanttTasks } from "./gantt-chart/data/ganttDatabaseService"
import { addWorkingDays, countWorkingDays } from "./schedule-date-engine"
import { useCompanyWorkingDays } from "./useCompanyWorkingDays"
import { useUser } from '@/contexts/UserContext'
import { buildFullAddress, fetchDailyWeatherForRange, geocodeAddressToCoords } from "../daily-logs/weatherAPI"
import { fetchEntity } from "@/lib/entity-api"
import { Textarea } from '@/components/ui/textarea';
import { CancelButton, SaveChangesButton, tertiaryHover } from '@/styles/StandardButtons';

interface CalendarViewProps {
  weekDates: Date[]
}

const formatDateKey = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getAllowedWeatherRange = () => {
  const today = new Date()
  const start = new Date(today.getFullYear(), today.getMonth(), 1)
  const end = new Date(today)
  end.setDate(end.getDate() + 14)
  return { start, end }
}

export function CalendarView({ weekDates }: CalendarViewProps) {
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [selectedGanttEvent, setSelectedGanttEvent] = useState<CalendarGanttEvent | null>(null)
  const [hiddenTasksModal, setHiddenTasksModal] = useState<{
    date: string;
    tasks: CalendarGanttEvent[];
  } | null>(null)
  const [taskCreationModal, setTaskCreationModal] = useState<{ date: string; isOpen: boolean } | null>(null)
  const [newTask, setNewTask] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    duration: 3,
    progress: 0,
    color: 'bg-blue-400'
  })
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [calendarData, setCalendarData] = useState<CalendarGanttDay[]>([])
  const [continuousBars, setContinuousBars] = useState<ContinuousBar[]>([])
  const [loading, setLoading] = useState(false)
  const [monthlyWeather, setMonthlyWeather] = useState<Record<string, { emoji: string; temp: string }>>({})
  const [weatherUnavailable, setWeatherUnavailable] = useState(false)
  const [weatherRange, setWeatherRange] = useState<{ start: string; end: string; projectId: string } | null>(null)
  const { selectedProject } = useProject()
  const { companyId, user } = useUser()
  // Single source of truth for working-day math (schedule-date-engine.ts,
  // same module Gantt uses) -- the quick "Add Task" form below used to
  // hand-roll its own calendar-day start/end/duration reconciliation,
  // which silently diverged from GanttTask.duration's actual meaning
  // (WORKING days) and ignored the company's configured working days.
  const { workingDays } = useCompanyWorkingDays(companyId)

  // Available color options for new tasks (matching Gantt chart)
  const colorOptions = [
    'bg-blue-400',
    'bg-green-400',
    'bg-yellow-500',
    'bg-red-500',
    'bg-purple-500',
    'bg-indigo-500',
    'bg-pink-500',
    'bg-teal-200',
    'bg-orange-500',
    'bg-cyan-600',
    'bg-emerald-800',
    'bg-gray-500',
  ]

  // Helper function to get a random color from available options
  const getRandomColor = () => {
    return colorOptions[Math.floor(Math.random() * colorOptions.length)];
  }

  // Generate full month dates
  const monthDates = useMemo(() => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    
    const startDate = new Date(firstDay)
    startDate.setDate(firstDay.getDate() - firstDay.getDay())
    
    const endDate = new Date(lastDay)
    endDate.setDate(lastDay.getDate() + (6 - lastDay.getDay()))
    
    const dates = []
    const current = new Date(startDate)
    
    while (current <= endDate) {
      dates.push(new Date(current))
      current.setDate(current.getDate() + 1)
    }
    
    return dates
  }, [currentMonth])

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showColorPicker) {
        const target = event.target as Element;
        if (!target.closest('.color-picker-container')) {
          setShowColorPicker(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColorPicker]);

  // Load Gantt data function
  const loadGanttData = useCallback(async () => {
    if (!selectedProject || selectedProject === "all") return
    setLoading(true)
    try {
      const startDate = monthDates[0]
      const endDate = monthDates[monthDates.length - 1]
      const { days, continuousBars: bars } = await getCalendarGanttData(
        selectedProject,
        startDate,
        endDate
      )
      setCalendarData(days)
      setContinuousBars(bars)
    } catch (error) {
      console.error("Error loading Gantt data:", error)
    } finally {
      setLoading(false)
    }
  }, [selectedProject, monthDates])

  // Load Gantt data on mount and when dependencies change
  useEffect(() => {
    loadGanttData()
  }, [loadGanttData])

  useEffect(() => {
    if (!selectedProject || selectedProject === "all" || !monthDates.length) {
      setMonthlyWeather({})
      setWeatherUnavailable(true)
      setWeatherRange(null)
      return
    }

    let cancelled = false

    const fetchWeather = async () => {
      try {
        if (!companyId) {
          setMonthlyWeather({})
          setWeatherUnavailable(true)
          setWeatherRange(null)
          return
        }

        const today = new Date()
        const { start: allowedStart, end: allowedEnd } = getAllowedWeatherRange()

        const firstDisplayed = monthDates[0]
        const lastDisplayed = monthDates[monthDates.length - 1]

        if (lastDisplayed < allowedStart || firstDisplayed > allowedEnd) {
          setMonthlyWeather({})
          setWeatherUnavailable(true)
          setWeatherRange(null)
          return
        }

        setWeatherUnavailable(false)

        let error: unknown = null
        const data = await fetchEntity<{ status?: string; address?: string; city?: string; state?: string; zip_code?: string; latitude?: number; longitude?: number }>('project', selectedProject)
          .then((project) => (project.status === 'active' ? project : null))
          .catch((e) => { error = e; return null })

        if (cancelled) return

        if (error) {
          console.error('Error fetching project address for weather', error)
          setMonthlyWeather({})
          setWeatherUnavailable(true)
          setWeatherRange(null)
          return
        }

        let coords: { latitude: number; longitude: number } | null = null

        if (typeof data?.latitude === 'number' && typeof data?.longitude === 'number') {
          coords = { latitude: data.latitude, longitude: data.longitude }
        } else {
          const fullAddress = data ? buildFullAddress([data.address, data.city, data.state, data.zip_code]) : ""
          if (!fullAddress) {
            setMonthlyWeather({})
            setWeatherUnavailable(true)
            setWeatherRange(null)
            return
          }

          coords = await geocodeAddressToCoords(fullAddress)
        }

        if (!coords) {
          setMonthlyWeather({})
          setWeatherUnavailable(true)
          setWeatherRange(null)
          return
        }
        if (cancelled) return

        const fetchStart = new Date(Math.max(allowedStart.getTime(), firstDisplayed.getTime()))
        const fetchEnd = new Date(Math.min(allowedEnd.getTime(), lastDisplayed.getTime()))

        if (fetchEnd < fetchStart) {
          setMonthlyWeather({})
          setWeatherUnavailable(true)
          setWeatherRange(null)
          return
        }

        const rangeStartKey = formatDateKey(fetchStart)
        const rangeEndKey = formatDateKey(fetchEnd)

        if (weatherRange && weatherRange.projectId === selectedProject && weatherRange.start === rangeStartKey && weatherRange.end === rangeEndKey && Object.keys(monthlyWeather).length) {
          setWeatherUnavailable(false)
          return
        }

        const nextWeather: Record<string, { emoji: string; temp: string }> = {}

        if (cancelled) return
        const summaries = await fetchDailyWeatherForRange(coords, fetchStart, fetchEnd)
        if (cancelled) return

        summaries.forEach((summary) => {
          const highC = Number.isFinite(summary.highC) ? summary.highC : null
          const lowC = Number.isFinite(summary.lowC) ? summary.lowC : null
          const highF = highC !== null ? Math.round((highC * 9) / 5 + 32) : null
          const lowF = lowC !== null ? Math.round((lowC * 9) / 5 + 32) : null
          const tempText = highF !== null && lowF !== null ? `${lowF}°F/${highF}°F` : ""
          nextWeather[summary.date] = {
            emoji: summary.emoji,
            temp: tempText,
          }
        })

        setMonthlyWeather(nextWeather)
        setWeatherRange({ start: rangeStartKey, end: rangeEndKey, projectId: selectedProject })
      } catch (err) {
        if (cancelled) return
        console.error('Error loading monthly weather data', err)
        setMonthlyWeather({})
        setWeatherUnavailable(true)
        setWeatherRange(null)
      }
    }

    fetchWeather()

    return () => {
      cancelled = true
    }
  }, [selectedProject, monthDates, companyId, currentMonth])

  // Navigation
  const goToPreviousMonth = () => {
    const newDate = new Date(currentMonth)
    newDate.setMonth(currentMonth.getMonth() - 1)
    setCurrentMonth(newDate)
  }
  const goToNextMonth = () => {
    const newDate = new Date(currentMonth)
    newDate.setMonth(currentMonth.getMonth() + 1)
    setCurrentMonth(newDate)
  }
  const goToCurrentMonth = () => setCurrentMonth(new Date())

  // Helpers
const getStatusBadge = (status: CalendarEvent["status"]) => {
  switch (status) {
    case "completed":
        return <Badge className="bg-green-500 text-white">Completed</Badge>
    case "in-progress":
        return <Badge className="bg-blue-500 text-white">In Progress</Badge>
    case "delayed":
      return <Badge variant="destructive">Delayed</Badge>
    default:
        return <Badge variant="outline" className="text-gray-700">Scheduled</Badge>
    }
  }

  const getGanttEventColor = (color: string, progress: number) => {
    const progressClass =
      progress === 100
        ? "border-green-500"
        : progress > 0
        ? "border-yellow-500"
        : "border-gray-300"
    return `${color} border-l-4 ${progressClass}`
  }

  const getProgressWidth = (progress: number) =>
    `${Math.max(0, Math.min(100, progress))}%`

  const formatDuration = (duration: number) =>
    duration === 1 ? "1 day" : `${duration} days`

  const formatMonthYear = (date: Date) =>
    date.toLocaleDateString("en-US", { month: "long", year: "numeric" })

  const getWeatherForDay = (date: Date) => {
    if (weatherUnavailable) {
      return { icon: null as string | null, temp: "Weather info n/a" }
    }

    const dateStr = date.toISOString().split('T')[0]
    const match = monthlyWeather[dateStr]
    if (!match) {
      return { icon: null as string | null, temp: "Weather info n/a" }
    }

    return {
      icon: match.emoji,
      temp: match.temp || "Weather info n/a",
    }
  }

  // Split bars into weekly segments
  const splitBarIntoWeeks = (bar: ContinuousBar) => {
    const startIndex = monthDates.findIndex(d => d.toISOString().split("T")[0] === bar.startDate)
    const endIndex = monthDates.findIndex(d => d.toISOString().split("T")[0] === bar.endDate)

    if (startIndex === -1 || endIndex === -1) return []

    const segments: { startDay: number; endDay: number; week: number }[] = []
    let current = startIndex
    while (current <= endIndex) {
      const currentWeek = Math.floor(current / 7)
      const weekEnd = Math.min(endIndex, (currentWeek + 1) * 7 - 1)
      segments.push({
        startDay: current % 7,
        endDay: weekEnd % 7,
        week: currentWeek
      })
      current = weekEnd + 1
    }
    return segments
  }

  // Handle task creation
  const handleCreateTask = async () => {
    if (!newTask.name.trim() || !selectedProject || selectedProject === 'all') return;

    try {
      // Load current project to find the last group
      const { loadGanttProject } = await import('./gantt-chart/data/ganttDatabaseService');
      const currentProject = await loadGanttProject(selectedProject);
      
      // Find the last group (groups have type 'group')
      const groups = currentProject?.tasks?.filter(task => task.type === 'group') || [];
      const lastGroup = groups.length > 0 ? groups[groups.length - 1] : null;
      
      // Create new task object
      const newTaskData = {
        id: crypto.randomUUID(),
        name: newTask.name.trim(),
        description: newTask.description.trim(),
        startDate: newTask.startDate,
        endDate: newTask.endDate,
        duration: newTask.duration,
        progress: newTask.progress,
        color: newTask.color,
        type: 'task' as const,
        parentId: lastGroup?.id || undefined, // Assign to last group or undefined
        level: lastGroup ? 1 : 0, // Level 1 if has parent, 0 if root
        isExpanded: true,
        sortOrder: 0, // Will be set to the end
        autoProgressUpdate: true,
        status: 'not-started' as const,
      };

      // Save to database
      await saveGanttTasks(selectedProject, [newTaskData], companyId || '', user?.id || '');

      // Reset form and close modal
      setNewTask({
        name: '',
        description: '',
        startDate: '',
        endDate: '',
        duration: 3,
        progress: 0,
        color: 'bg-blue-400'
      });
      setTaskCreationModal(null);

      // Reload calendar data
      await loadGanttData();
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Failed to create task. Please try again.');
    }
  };

  // Week heights (auto expand rows)
   // Calculate week heights correctly
   const weekHeights = useMemo(() => {
     // All weeks have the same height
     return Array(6).fill(140) // Fixed height for all weeks
   }, [])

  // Timeline-aware slot allocator with persistent slot assignment
  const allocateSlots = (bars: ContinuousBar[], monthDates: Date[], maxVisible: number = 2) => {
    const visibleSegments: Array<{
      task: CalendarGanttEvent;
      startDate: string;
      endDate: string;
      slot: number;
    }> = [];
    const hiddenByDay: Record<string, CalendarGanttEvent[]> = {};
    
    // Track which slots are currently occupied by which tasks
    const slotAssignments: Record<number, string> = {}; // slot -> taskId
    const taskToSlot: Record<string, number> = {}; // taskId -> slot

    monthDates.forEach((date, dateIndex) => {
      const dateStr = date.toISOString().split("T")[0];
      // Normalize all comparisons to midnight to avoid TZ off-by-one
      const day = new Date(dateStr + 'T00:00:00');
      const activeBars = bars.filter(b => {
        const start = new Date(b.startDate + 'T00:00:00');
        const end = new Date(b.endDate + 'T00:00:00');
        return day >= start && day <= end; // inclusive
      });

      // Sort by startDate → earlier tasks have higher priority
      activeBars.sort((a, b) => a.startDate.localeCompare(b.startDate));

      // First, check if any currently visible tasks have ended
      Object.keys(slotAssignments).forEach(slotStr => {
        const slot = parseInt(slotStr, 10);
        const taskId = slotAssignments[slot];
        const taskStillActive = activeBars.some(bar => bar.task.id === taskId);
        if (!taskStillActive) {
          // Task ended, free up the slot
          delete slotAssignments[slot];
          delete taskToSlot[taskId];
        }
      });

      // Process each active task
      activeBars.forEach((bar) => {
        const taskId = bar.task.id;
        
        // Check if this task already has a slot assignment
        if (taskToSlot[taskId] !== undefined) {
          // Task already has a slot, extend its segment
          const existingSegment = visibleSegments.find(
            seg => seg.task.id === taskId && 
                   seg.endDate === (dateIndex > 0 ? monthDates[dateIndex - 1].toISOString().split("T")[0] : null)
          );
          
          if (existingSegment) {
            existingSegment.endDate = dateStr;
          } else {
            // This shouldn't happen, but create a new segment if needed
            visibleSegments.push({
              task: bar.task,
              startDate: dateStr,
              endDate: dateStr,
              slot: taskToSlot[taskId]
            });
          }
        } else {
          // Task doesn't have a slot yet, try to assign one
          let assignedSlot = -1;
          
          // Find an available slot
          for (let slot = 0; slot < maxVisible; slot++) {
            if (slotAssignments[slot] === undefined) {
              assignedSlot = slot;
              break;
            }
          }
          
          if (assignedSlot !== -1) {
            // Assign slot to this task
            slotAssignments[assignedSlot] = taskId;
            taskToSlot[taskId] = assignedSlot;
            
            // Create new segment
            visibleSegments.push({
              task: bar.task,
              startDate: dateStr,
              endDate: dateStr,
              slot: assignedSlot
            });
            } else {
              // No available slots, add task only for this current day
              hiddenByDay[dateStr] = hiddenByDay[dateStr] || [];
              if (!hiddenByDay[dateStr].some(t => t.id === bar.task.id)) {
                hiddenByDay[dateStr].push(bar.task);
              }
            }
        }
      });
    });

    return { visibleSegments, hiddenByDay };
  };

  // Calculate slot allocation once
  const slotAllocation = useMemo(() => {
    if (!continuousBars.length || !monthDates.length) {
      return { visibleSegments: [], hiddenByDay: {} };
    }
    return allocateSlots(continuousBars, monthDates);
  }, [continuousBars, monthDates]);

  // Render visible segments from slot allocator
  const renderVisibleSegments = (visibleSegments: Array<{
    task: CalendarGanttEvent;
    startDate: string;
    endDate: string;
    slot: number;
  }>) => {
    return visibleSegments.map((segment, index) => {
      // Find the week and day positions for this segment
      const startDate = new Date(segment.startDate);
      const endDate = new Date(segment.endDate);
      
      const startIndex = monthDates.findIndex(d => d.toISOString().split('T')[0] === segment.startDate);
      const endIndex = monthDates.findIndex(d => d.toISOString().split('T')[0] === segment.endDate);
      
      if (startIndex === -1 || endIndex === -1) {
        return null;
      }
      
      const startWeek = Math.floor(startIndex / 7);
      const endWeek = Math.floor(endIndex / 7);
      const startDay = startIndex % 7;
      const endDay = endIndex % 7;
      
      // For multi-week segments, we need to render multiple bars
      if (startWeek === endWeek) {
        // Single week segment - ensure it covers the full width of the ending date box
        const gridColumn = `${startDay + 1} / ${endDay + 2}`;
        
        return (
          <div
            key={`${segment.task.id}-${index}`}
            className={`h-5 rounded-full cursor-pointer hover:shadow-sm text-xs flex items-center relative overflow-hidden ${segment.task.color}`}
            style={{
              gridColumn,
              gridRow: startWeek + 2,
              marginTop: `${3.5 + segment.slot * 1.6}rem`,
              marginLeft: '0.25rem',   // ⬅️ add this
              marginRight: '0.25rem',   // ⬅️ add this
              position: "relative",
              zIndex: 'var(--z-sticky)'
            }}
            onClick={() => setSelectedGanttEvent(segment.task)}
          >
            {/* Progress overlay - same as Gantt chart */}
            <div 
              className="h-full rounded-full bg-black/20"
              style={{ 
                width: `${segment.task.progress}%`,
                marginLeft: '0.25rem', // Match pills spacing
                marginRight: '0.25rem' // Match pills spacing
              }}
            />
            <div 
              className="truncate text-white font-medium absolute left-0 top-0 bottom-0 flex items-center z-10"
              style={{
                paddingLeft: '0.25rem', // Match pills spacing
                paddingRight: '0.25rem' // Match pills spacing
              }}
            >
              {segment.task.name}
            </div>
          </div>
        );
      } else {
        // Multi-week segment - render multiple bars for each week
        const bars = [];
        
        // First week: from start day to end of week
        bars.push(
          <div
            key={`${segment.task.id}-${index}-week-${startWeek}`}
            className={`h-5 rounded-full cursor-pointer hover:shadow-sm text-xs flex items-center relative overflow-hidden ${segment.task.color}`}
            style={{
              gridColumn: `${startDay + 1} / 8`, // Span to end of week
              gridRow: startWeek + 2,
              marginTop: `${3.5 + segment.slot * 1.6}rem`,
              marginLeft: '0.25rem',   // ⬅️ add this
              marginRight: '0.25rem',   // ⬅️ add this
              position: "relative",
              zIndex: 'var(--z-sticky)'
            }}
            onClick={() => setSelectedGanttEvent(segment.task)}
          >
            {/* Progress overlay - same as Gantt chart */}
            <div 
              className="h-full rounded-full bg-black/20"
              style={{ 
                width: `${segment.task.progress}%`,
                marginLeft: '0.0rem', // Match pills spacing
                marginRight: '0.0rem' // Match pills spacing
              }}
            />
            <div 
              className="truncate text-white font-medium absolute left-0 top-0 bottom-0 flex items-center z-10"
              style={{
                paddingLeft: '0.25rem', // Match pills spacing
                paddingRight: '0.25rem' // Match pills spacing
              }}
            >
              {segment.task.name}
            </div>
          </div>
        );
        
        // Middle weeks: full width
        for (let week = startWeek + 1; week < endWeek; week++) {
          bars.push(
            <div
              key={`${segment.task.id}-${index}-week-${week}`}
              className={`h-5 rounded-full cursor-pointer hover:shadow-sm text-xs flex items-center relative overflow-hidden ${segment.task.color}`}
              style={{
                gridColumn: `1 / 8`, // Full width
                gridRow: week + 2,
                marginTop: `${3.5 + segment.slot * 1.6}rem`,
                marginLeft: '0.25rem',   // ⬅️ add this
                marginRight: '0.25rem',   // ⬅️ add this
                position: "relative",
                zIndex: 'var(--z-sticky)'
              }}
              onClick={() => setSelectedGanttEvent(segment.task)}
            >
              {/* Progress overlay - same as Gantt chart */}
              <div 
                className="h-full rounded-full bg-black/20"
                style={{ 
                  width: `${segment.task.progress}%`,
                  marginLeft: '0.25rem', // Match pills spacing
                  marginRight: '0.25rem' // Match pills spacing
                }}
              />
              <div 
                className="truncate text-white font-medium absolute left-0 top-0 bottom-0 flex items-center z-10"
                style={{
                  paddingLeft: '0.25rem', // Match pills spacing
                  paddingRight: '0.25rem' // Match pills spacing
                }}
              >
                {segment.task.name}
              </div>
            </div>
          );
        }
        
        // Last week: from start of week to end day
        bars.push(
          <div
            key={`${segment.task.id}-${index}-week-${endWeek}`}
            className={`h-5 rounded-full cursor-pointer hover:shadow-sm text-xs flex items-center relative overflow-hidden ${segment.task.color}`}
            style={{
              gridColumn: `1 / ${endDay + 2}`, // From start to end day
              gridRow: endWeek + 2,
              marginTop: `${3.5 + segment.slot * 1.6}rem`,
              marginLeft: '0.25rem',   // ⬅️ add this
              marginRight: '0.25rem',   // ⬅️ add this
              position: "relative",
              zIndex: 'var(--z-sticky)'
            }}
            onClick={() => setSelectedGanttEvent(segment.task)}
          >
            {/* Progress overlay - same as Gantt chart */}
            <div 
              className="h-full rounded-full bg-black/20"
              style={{ 
                width: `${segment.task.progress}%`,
                marginLeft: '0.0rem', // Match pills spacing
                marginRight: '0.0rem' // Match pills spacing
              }}
            />
            <div 
              className="truncate text-white font-medium absolute left-0 top-0 bottom-0 flex items-center z-10"
  style={{
                paddingLeft: '0.25rem', // Match pills spacing
                paddingRight: '0.25rem' // Match pills spacing
              }}
            >
              {segment.task.name}
            </div>
          </div>
        );
        
        return bars;
      }
    }).filter(Boolean);
  };




  return (
    <div className="space-y-4">
      {/* Navigation */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">{formatMonthYear(currentMonth)}</h2>
        <div className="flex gap-2">
          <button onClick={goToPreviousMonth} className="p-2 rounded-lg" {...tertiaryHover}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={goToCurrentMonth} className="px-3 py-1 bg-gray-100 rounded-lg" {...tertiaryHover}>
            This Month
          </button>
          <button onClick={goToNextMonth} className="p-2 rounded-lg" {...tertiaryHover}>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Calendar Grid with overlay */}
      <div className="relative">
        {/* Base day grid */}
        <div
          className="grid grid-cols-7 border border-gray-200"
          style={{
            gridTemplateRows: `auto ${weekHeights.map(h => `${h}px`).join(" ")}`
          }}
        >
          {/* Day headers */}
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
            <div
              key={day}
              className="p-2 text-center text-sm font-medium bg-gray-50 border-r border-b border-gray-200 last:border-r-0"
            >
            {day}
          </div>
        ))}

          {/* Calendar days */}
        {monthDates.map((date, index) => {
          const isCurrentMonth = date.getMonth() === currentMonth.getMonth()
          const isToday = date.toDateString() === new Date().toDateString()
          const weather = getWeatherForDay(date)
          const dayOfWeek = date.getDay() // 0 = Sunday, 6 = Saturday
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
        return (
              <div
              key={index} 
                className={`min-h-[7.5rem] p-1 relative border border-gray-200 cursor-pointer hover:bg-gray-50 ${
                  !isCurrentMonth ? "bg-gray-50" : isWeekend ? "bg-gray-100" : isToday ? "bg-blue-100" : "bg-white"
                }`}
                onClick={() => {
                  const dateStr = date.toISOString().split('T')[0];
                  const startDate = new Date(dateStr + 'T00:00:00');
                  // Default 3 WORKING days, same engine/meaning as everywhere else.
                  const endDate = addWorkingDays(startDate, 3, workingDays);

                  setNewTask(prev => ({
                    ...prev,
                    startDate: dateStr,
                    endDate: endDate.toISOString().split('T')[0],
                    duration: 3,
                    color: getRandomColor()
                  }));
                  setTaskCreationModal({ date: dateStr, isOpen: true });
                }}
              >
                 <div className="flex flex-col items-center text-center mb-0">
                   <div className={`text-sm font-medium ${isCurrentMonth ? "text-gray-900" : "text-gray-400"} ${isToday ? "text-blue-600 font-bold" : ""}`}>
                    {date.getDate()}
                </div>
                   <div className="flex items-center gap-1 text-xs text-gray-500">
                     {weather.icon ? <span>{weather.icon}</span> : null}
                     <span>{weather.temp}</span>
                   </div>
                 </div>
              </div>
            )
          })}
              </div>

        {/* Combined Bars and Pills Overlay */}
        <div
          className="absolute inset-0 grid grid-cols-7 pointer-events-none"
          style={{
            gridTemplateRows: `auto ${weekHeights.map(h => `${h}px`).join(" ")}`
          }}
        >
          {/* Render bars directly in grid positions */}
          {renderVisibleSegments(slotAllocation.visibleSegments).flat()}
          
          {/* Render pills directly in grid positions */}
          {(() => {
            const { hiddenByDay } = slotAllocation;
            return monthDates.map((date, index) => {
              const dateStr = date.toISOString().split('T')[0];
              const weekIndex = Math.floor(index / 7);
              const dayInWeek = index % 7;
              
                const hiddenTasks = hiddenByDay[dateStr] || [];
               
                // Show pill if there are hidden tasks
              if (hiddenTasks.length === 0) return null;
              
              return (
                <div
                  key={`more-${index}`}
                  className="flex items-start justify-start pointer-events-auto z-20"
                  style={{
                    gridColumn: dayInWeek + 1,
                    gridRow: weekIndex + 2,
                    marginTop: `${3.5 + 2 * 1.6}rem`, // Position in 3rd slot
                    paddingLeft: '0.25rem', // Left align within the box
                  }}
                >
                  <button
                    onClick={() => setHiddenTasksModal({ date: dateStr, tasks: hiddenTasks })}
                    className="bg-gray-200 text-black text-xs px-2 py-1 rounded-full transition-colors"
                    {...tertiaryHover}
                  >
                    +{hiddenTasks.length}
                  </button>
                </div>
              );
            });
          })()}
        </div>
      </div>

      {/* Gantt Event Modal */}
      {selectedGanttEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold">{selectedGanttEvent.name}</h3>
              <button onClick={() => setSelectedGanttEvent(null)} className="text-gray-400">
                <X className="h-5 w-5" />
              </button>
                      </div>
                      <div className="space-y-3">
              <div><span className="font-medium">Duration:</span> {formatDuration(selectedGanttEvent.duration)}</div>
              <div><span className="font-medium">Start:</span> {new Date(selectedGanttEvent.startDate).toLocaleDateString()}</div>
              <div><span className="font-medium">End:</span> {new Date(selectedGanttEvent.endDate).toLocaleDateString()}</div>
                        <div>
                <span className="font-medium">Progress:</span>
                <div className="mt-1">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{ width: getProgressWidth(selectedGanttEvent.progress) }} />
                        </div>
                  <span className="text-xs">{selectedGanttEvent.progress}%</span>
                </div>
                          </div>
              {selectedGanttEvent.description && (
                <div>
                  <span className="font-medium">Description:</span>
                  <p className="text-sm text-gray-600 mt-1">{selectedGanttEvent.description}</p>
                          </div>
                        )}
                      </div>
          </div>
        </div>
      )}

      {/* Hidden Tasks Modal */}
      {hiddenTasksModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full mx-4">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold">
                Additional Tasks - {new Date(hiddenTasksModal.date).toLocaleDateString()}
              </h3>
              <button onClick={() => setHiddenTasksModal(null)} className="text-gray-400">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {hiddenTasksModal.tasks.map((task) => (
                <div
                  key={task.id}
                  className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setSelectedGanttEvent(task);
                    setHiddenTasksModal(null);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded ${getGanttEventColor(task.color, task.progress)}`} />
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{task.name}</h4>
                      <div className="text-xs text-gray-500">
                        {new Date(task.startDate + 'T00:00:00').toLocaleDateString()} - {new Date(task.endDate + 'T00:00:00').toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDuration(task.duration)} • {task.progress}% complete
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Task Creation Modal */}
      {taskCreationModal?.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              Create New Task for {new Date(taskCreationModal.date).toLocaleDateString()}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Task Name *
                </label>
                <input
                  type="text"
                  value={newTask.name}
                  onChange={(e) => setNewTask(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter task name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <Textarea
                  compact
                  value={newTask.description}
                  onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter task description"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={newTask.startDate}
                    onChange={(e) => {
                      const startDate = e.target.value;
                      const startDateObj = new Date(startDate + 'T00:00:00');
                      // Same reconciliation rule as useGanttData.updateTask:
                      // start changes, duration (working days) is preserved,
                      // end is re-derived from it.
                      const endDateObj = addWorkingDays(startDateObj, newTask.duration, workingDays);
                      setNewTask(prev => ({
                        ...prev,
                        startDate,
                        endDate: endDateObj.toISOString().split('T')[0]
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={newTask.endDate}
                    onChange={(e) => {
                      const endDate = e.target.value;
                      const startDateObj = new Date(newTask.startDate + 'T00:00:00');
                      const endDateObj = new Date(endDate + 'T00:00:00');
                      // Same reconciliation rule as useGanttData.updateTask:
                      // end changes, duration (working days) is re-derived.
                      const duration = countWorkingDays(startDateObj, endDateObj, workingDays);
                      setNewTask(prev => ({
                        ...prev,
                        endDate,
                        duration
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (working days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={newTask.duration}
                    onChange={(e) => {
                      const duration = parseInt(e.target.value) || 3;
                      const startDate = new Date(newTask.startDate + 'T00:00:00');
                      // Same reconciliation rule as useGanttData.updateTask:
                      // duration (working days) changes, end is re-derived.
                      const endDate = addWorkingDays(startDate, duration, workingDays);
                      setNewTask(prev => ({
                        ...prev,
                        duration,
                        endDate: endDate.toISOString().split('T')[0]
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Progress (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={newTask.progress}
                    onChange={(e) => setNewTask(prev => ({ ...prev, progress: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Color Picker (swatch opens palette) */}
              <div className="relative color-picker-container" onClick={(e) => e.stopPropagation()}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <button
                  type="button"
                  onClick={() => setShowColorPicker(prev => !prev)}
                  className="h-6 w-6 rounded-full border ring-1 ring-gray-300"
                  aria-label="Current color"
                  key={`color-swatch-${newTask.color}`}
                >
                  <span className={`block h-full w-full rounded-full ${newTask.color}`}></span>
                </button>
                {showColorPicker && (
                  <div className="absolute right-0 mt-2 p-4 bg-white border border-gray-200 rounded shadow-md grid grid-cols-4 gap-4 z-50 justify-items-center" style={{ minWidth: '12rem' }}>
                    {colorOptions.map((cls) => (
                      <button
                        key={cls}
                        type="button"
                        onClick={() => {
                          setNewTask(prev => ({ ...prev, color: cls }));
                          setShowColorPicker(false);
                        }}
                        className={`h-6 w-6 rounded-full border ${newTask.color === cls ? 'ring-2 ring-blue-600' : ''} ${cls} m-1`}
                        aria-label={cls}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <CancelButton
                className="flex-1"
                onClick={() => {
                  setTaskCreationModal(null);
                  // Reset form to default values (keep start date and duration)
                  setNewTask(prev => ({
                    name: '',
                    description: '',
                    startDate: prev.startDate, // Keep the clicked date
                    endDate: prev.endDate, // Keep the calculated end date
                    duration: 3,
                    progress: 0,
                    color: 'bg-blue-400'
                  }));
                }}
              />
              <SaveChangesButton type="button" className="flex-1" onClick={handleCreateTask} disabled={!newTask.name.trim()}>
                Create Task
              </SaveChangesButton>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center py-8 text-gray-500">
          Loading Gantt data...
        </div>
      )}
    </div>
  )
}
